import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { WebsiteNote, WebsiteName } from '@/types/website';

export function useWebsiteNotes(websiteFilter?: WebsiteName) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading, error } = useQuery({
    queryKey: ['website-notes', websiteFilter],
    queryFn: async () => {
      let query = supabase
        .from('website_notes')
        .select(`
          *,
          created_profile:profiles!website_notes_created_by_fkey(display_name, email)
        `)
        .order('created_at', { ascending: false });

      if (websiteFilter) {
        query = query.eq('website', websiteFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as WebsiteNote[];
    },
    enabled: !!user,
  });

  const createNote = useMutation({
    mutationFn: async (note: {
      note: string;
      website: WebsiteName;
      labels?: string[];
    }) => {
      const { data, error } = await supabase
        .from('website_notes')
        .insert({
          ...note,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-notes'] });
      toast.success('Notitie aangemaakt');
    },
    onError: (error) => {
      console.error('Error creating note:', error);
      toast.error('Fout bij aanmaken notitie');
    },
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WebsiteNote> & { id: string }) => {
      const { data, error } = await supabase
        .from('website_notes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-notes'] });
      toast.success('Notitie bijgewerkt');
    },
    onError: (error) => {
      console.error('Error updating note:', error);
      toast.error('Fout bij bijwerken notitie');
    },
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('website_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-notes'] });
      toast.success('Notitie verwijderd');
    },
    onError: (error) => {
      console.error('Error deleting note:', error);
      toast.error('Fout bij verwijderen notitie');
    },
  });

  // Get all unique labels for filtering
  const allLabels = [...new Set(notes.flatMap(n => n.labels || []))];

  return {
    notes,
    isLoading,
    error,
    allLabels,
    createNote,
    updateNote,
    deleteNote,
  };
}
